<template>
  <page
    :name="$t('Offerte e ordini')"
    :description="$t('Qui puoi trovare tutti i nostri preventivi e le offerte generate da wizard')"
    :empty-text="$t('Nessuna offerta disponibile')"
    :empty="!filteredOffers.length"
    :state="offersState"
  >
    <template #toolbar-post>
      <pf-toolbar-item>
        <pf-dropdown id="offers-filter-dropdown" :text="$t('Filtra per stato') + ': ' + filterLabel(offerFilter)">
          <pf-dropdown-item @click.prevent="offerFilter = 'all'">
            {{ filterLabel('all') }}
          </pf-dropdown-item>
          <pf-dropdown-item @click.prevent="offerFilter = 'in_corso'">
            {{ filterLabel('in_corso') }}
          </pf-dropdown-item>
          <pf-dropdown-item @click.prevent="offerFilter = 'ordine'">
            {{ filterLabel('ordine') }}
          </pf-dropdown-item>
          <pf-dropdown-item @click.prevent="offerFilter = 'rifiutata'">
            {{ filterLabel('rifiutata') }}
          </pf-dropdown-item>
        </pf-dropdown>
      </pf-toolbar-item>
    </template>

    <pf-page-section no-body-wrapper>
      <pf-data-list expandable>
        <pf-data-list-item v-for="row in filteredOffers" :key="row.id">
          <pf-data-list-cell class="pf-v6-u-display-none-on-md pf-v6-u-display-inline">
            <pf-label v-if="row.stato === 'in_corso'" color="yellow">{{ $t('Offerta in corso') }}</pf-label>
            <pf-label v-else-if="row.stato === 'ordine'" color="green">{{ $t('Ordine completato') }}</pf-label>
            <pf-label v-else-if="row.stato === 'rifiutata'" color="red">{{ $t('Offerta rifiutata') }}</pf-label>
          </pf-data-list-cell>
          <pf-data-list-cell :data-ouia-component-id="`offers-title-${row.id}`">
            <template v-if="row.titolo_offerta">
              <pf-title h="4">{{ row.titolo_offerta }}</pf-title>
              <p>{{ $t('Offerta nr. {0}', [row.id]) }}</p>
            </template>
            <pf-title h="4" v-else>{{ $t('Offerta nr. {0}', [row.id]) }}</pf-title>
          </pf-data-list-cell>

          <pf-data-list-cell :data-ouia-component-id="`offers-date-${row.id}`">
            <pf-title h="4">{{ $t('Data') }}</pf-title>
            {{ $date(row.data) }}
          </pf-data-list-cell>

          <pf-data-list-cell :data-ouia-component-id="`offers-customer-${row.id}`">
            <pf-title h="4">{{ $t('Intestata a') }}</pf-title>
            <p>{{ row.ragione_sociale }}</p>
          </pf-data-list-cell>

          <pf-data-list-cell :data-ouia-component-id="`offers-amount-${row.id}`">
            <pf-title h="4">{{ $t('Importo') }}</pf-title>
            <p>
              {{ $currency(row.gran_totale, row.currency) }} <span class="small" v-if="row.gran_totale_ivato - row.gran_totale > 0">+ {{ $t('IVA') }}</span>
            </p>
          </pf-data-list-cell>

          <pf-data-list-action class="pf-v6-u-flex-direction-column pf-v6-u-flex-direction-row-on-md">
            <pf-button variant="tertiary" small target="_blank" :href="`/rest/offer-pdf/${row.id}`" :title="$t('Scarica PDF')">
              <template #icon><file-pdf-icon /></template>
            </pf-button>

            <pf-button
              v-if="row.stato === 'in_corso'"
              :disabled="!row.enable_orders"
              variant="danger"
              small
              @click="rejectedId = row.id"
              :title="$t('Rifiuta offerta')"
            >
              <template #icon><ban-icon /></template>
              <form-help-tooltip
                v-if="!row.enable_orders"
                :text="$t('Non hai i permessi per rifiutare quest\'offerta. Richiedili ad un utente amministratore dell\'azienda intestataria.')"
              />
            </pf-button>

            <granted-link
              v-if="row.stato === 'in_corso'"
              :force-disabled="!row.enable_orders"
              :to="{ name: 'confirm_offer', params: { id: row.id } }"
              :disabled-message="$t('Non hai i permessi per confermare l\'offerta. Richiedili ad un utente amministratore dell\'azienda intestataria.')"
              type="primary"
              small
              :title="$t('Conferma ordine')"
            >
              <template #icon><check-icon /></template>
              <span class="pf-v6-u-display-none pf-v6-u-display-inline-on-md">{{ $t('Conferma ordine') }}</span>
            </granted-link>
            <pf-label v-else-if="row.stato === 'ordine'" color="green" class="pf-v6-u-display-none pf-v6-u-display-inline-on-md">{{
              $t('Ordine completato')
            }}</pf-label>
            <pf-label v-else-if="row.stato === 'rifiutata'" color="red" class="pf-v6-u-display-none pf-v6-u-display-inline-on-md">{{
              $t('Offerta rifiutata')
            }}</pf-label>
          </pf-data-list-action>

          <pf-data-list-content>
            <pf-title h="3">{{ row.stato === 'ordine' ? $t('Dettaglio ordine') : $t('Dettaglio offerta') }}</pf-title>
            <offer-detail :offerta="row" />

            <pf-data-list-action class="pf-v6-u-flex-direction-column pf-v6-u-flex-direction-row-on-sm pf-v6-u-mt-md">
              <pf-button variant="tertiary" target="_blank" :href="`/rest/offer-pdf/${row.id}`" :title="$t('Scarica PDF')">
                <template #icon><file-pdf-icon /></template>
                {{ $t('Scarica PDF') }}
              </pf-button>

              <pf-button
                v-if="row.stato === 'in_corso'"
                :disabled="!row.enable_orders"
                variant="danger"
                @click="rejectedId = row.id"
                :title="$t('Rifiuta offerta')"
              >
                <template #icon><ban-icon /></template>
                {{ $t('Rifiuta offerta') }}
                <form-help-tooltip
                  v-if="!row.enable_orders"
                  :text="$t('Non hai i permessi per rifiutare quest\'offerta. Richiedili ad un utente amministratore dell\'azienda intestataria.')"
                />
              </pf-button>

              <granted-link
                v-if="row.stato === 'in_corso'"
                :force-disabled="!row.enable_orders"
                :to="{ name: 'confirm_offer', params: { id: row.id } }"
                :disabled-message="$t('Non hai i permessi per confermare l\'offerta. Richiedili ad un utente amministratore dell\'azienda intestataria.')"
                type="primary"
                :title="$t('Conferma ordine')"
              >
                <template #icon><check-icon /></template>
                {{ $t('Conferma ordine') }}
              </granted-link>
            </pf-data-list-action>
          </pf-data-list-content>
        </pf-data-list-item>
      </pf-data-list>

      <x-form
        v-if="rejectedId"
        modal
        open
        :title="$t('Rifiuta l\'offerta')"
        variant="small"
        :submit-text="$t('Rifiuta')"
        @submit="submit"
        @close="rejectedId = undefined"
      >
        <form-group :name="$t('Motivazione rifiuto')" required>
          <pf-form-select
            :model-value="motivazioneRifiuto"
            @update:model-value="motivazioneRifiuto = $as<MotivazioniRifiuto>($event)"
            name="motivazione_rifiuto"
            required
          >
            <pf-form-select-option v-for="(m, key) in motivazioni" :key="key" :value="key">
              {{ m }}
            </pf-form-select-option>
          </pf-form-select>
        </form-group>
      </x-form>
    </pf-page-section>
  </page>
</template>

<script lang="ts" setup>
import { ref, type Ref, computed } from 'vue';
import { $t as _t } from '@/i18n';
import { asyncComputedList } from '@common/asyncComputed';
import { OffersResource, type MotivazioniRifiuto } from '@/resources';
import OfferDetail from '@/components/offer-detail.vue';
import GrantedLink from '@/components/granted-link.vue';
import FormHelpTooltip from '@/components/FormHelpTooltip.vue';
import FilePdfIcon from '@vue-patternfly/icons/file-pdf-icon';
import BanIcon from '@vue-patternfly/icons/ban-icon';
import CheckIcon from '@vue-patternfly/icons/check-icon';

const { state: offersState, result: offers } = asyncComputedList(() => new OffersResource().get());

type OfferFilter = 'all' | 'in_corso' | 'ordine' | 'rifiutata';
const offerFilter: Ref<OfferFilter> = ref('all');

function filterLabel(filter: OfferFilter) {
  switch (filter) {
    case 'all':
      return _t('Tutti');
    case 'in_corso':
      return _t('Offerta in corso');
    case 'ordine':
      return _t('Ordine completato');
    case 'rifiutata':
      return _t('Offerta rifiutata');
  }
}

const filteredOffers = computed(() => offers.value.filter(o => offerFilter.value === 'all' || o.stato === offerFilter.value));

const motivazioni: Record<MotivazioniRifiuto, string> = {
  prezzo: _t('Prezzo superiore alle aspettative'),
  'altro fornitore': _t('Ho scelto un altro fornitore'),
  tempistiche: _t('Tempistiche di risposta commerciali non adeguate'),
  'non più necessario': _t('I prodotti richiesti non sono più necessari'),
  altro: _t('Altro'),
};

const rejectedId: Ref<number | undefined> = ref();
const motivazioneRifiuto: Ref<MotivazioniRifiuto | undefined> = ref();

async function submit() {
  if (rejectedId.value && motivazioneRifiuto.value) {
    await new OffersResource().put(rejectedId.value, { stato: 'rifiutata', motivazione_rifiuto: motivazioneRifiuto.value });

    rejectedId.value = undefined;

    offersState.update();
  }
}
</script>
